import React, { useMemo, useState } from 'react'
import PageHeader from '@/components/Common/PageHeader'
import SEOHead from '@/components/SEO/SEOHead'
import { buildGraph, breadcrumbListData, itemListSchema } from '@/data/structured-data'
import { allBreeds } from '@/data/breeds'
import { useSearchParams } from 'react-router-dom'

const PetsPage: React.FC = () => {
  return (
    <>
      <SEOHead
        title="Can Dostlarımız | Ardıç Veteriner Kliniği"
        description="Kedi ve köpek ırkları rehberi: karakter özellikleri, bakım ihtiyaçları, yaşam süresi ve sağlık notları. Şanlıurfa Ardıç Veteriner Kliniği."
        keywords="kedi ırkları, köpek ırkları, kedi bakımı, köpek bakımı, evcil hayvanlar kliniği, evcil veteriner kliniği"
        canonicalUrl="/pets"
        structuredData={buildGraph(
          itemListSchema({
            name: 'Kedi ve Köpek Irkları',
            url: '/pets',
            items: allBreeds.map(b => ({ name: b.name, url: '/pets' }))
          }),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Can Dostlarımız', url: '/pets' }
          ])
        )}
      />
      <PageHeader title="Can Dostlarımız" subtitle="Kedi ve köpek türleri hakkında kısa bilgiler ve bakım ipuçları yakında." />
      <section className="section-padding">
        <div className="container-custom">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <FilterChips />
          </div>

          {/* Breed Grid */}
          <BreedGrid />
        </div>
      </section>
    </>
  )
}

export default PetsPage

// Local components
const FilterChips: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const current = searchParams.get('type')
  const type: 'all' | 'cat' | 'dog' = current === 'cat' || current === 'dog' ? current : 'all'

  const updateType = (t: 'all' | 'cat' | 'dog') => {
    const next = new URLSearchParams(searchParams)
    if (t === 'all') next.delete('type')
    else next.set('type', t)
    setSearchParams(next)
  }

  const chip = (label: string, value: 'all' | 'cat' | 'dog') => (
    <button
      key={value}
      onClick={() => updateType(value)}
      className={`px-4 py-2 rounded-full border text-sm ${type===value? 'bg-primary-600 text-white border-primary-600':'bg-white hover:bg-gray-50'}`}
    >
      {label}
    </button>
  )

  return (
    <div className="flex items-center gap-2">
      {chip('Tümü', 'all')}
      {chip('Kediler', 'cat')}
      {chip('Köpekler', 'dog')}
    </div>
  )
}

const BreedGrid: React.FC = () => {
  const [query, setQuery] = useState('')
  const [searchParams] = useSearchParams()
  const current = searchParams.get('type')
  const type: 'all' | 'cat' | 'dog' = current === 'cat' || current === 'dog' ? current : 'all'

  const list = useMemo(() => {
    const base = type === 'all' ? allBreeds : allBreeds.filter(b => b.type === type)
    return base.filter(b => b.name.toLowerCase().includes(query.toLowerCase()))
  }, [type, query])

  return (
    <>
      <div className="mb-4">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Tür ara..."
          className="w-full md:w-80 px-4 py-2 border rounded-lg"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {list.map(b => (
          <div key={b.id} className="bg-white rounded-xl border p-4 shadow hover:shadow-lg transition">
            <img
              src={b.image}
              alt={b.name}
              loading="lazy"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = b.type === 'dog' ? '/golden-retriever.webp' : '/persian.webp'
              }}
              className="w-full h-72 object-cover rounded-lg mb-3"
            />
            <h3 className="text-lg font-bold mb-1">{b.name}</h3>
            <p className="text-gray-600 text-sm mb-3">{b.description}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {b.characteristics.map((c, i) => (
                <span key={i} className="px-2 py-1 rounded-full bg-primary-50 text-primary-700 text-xs border border-primary-200">{c}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {b.careNotes.map((c, i) => (
                <span key={i} className="px-2 py-1 rounded-full bg-gray-50 text-gray-700 text-xs border">{c}</span>
              ))}
            </div>
            {/* Extra details */}
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
              {b.weightKg && (
                <div><span className="font-semibold">Kilo:</span> {b.weightKg[0]}–{b.weightKg[1]} kg</div>
              )}
              {b.heightCm && (
                <div><span className="font-semibold">Yükseklik:</span> {b.heightCm[0]}–{b.heightCm[1]} cm</div>
              )}
              {b.lifeSpanYears && (
                <div><span className="font-semibold">Yaşam:</span> {b.lifeSpanYears[0]}–{b.lifeSpanYears[1]} yıl</div>
              )}
              {b.grooming && (
                <div><span className="font-semibold">Bakım:</span> {b.grooming === 'low' ? 'Düşük' : b.grooming === 'medium' ? 'Orta' : 'Yüksek'}</div>
              )}
              {b.activity && (
                <div><span className="font-semibold">Aktivite:</span> {b.activity === 'low' ? 'Düşük' : b.activity === 'medium' ? 'Orta' : 'Yüksek'}</div>
              )}
              {typeof b.familyFriendly !== 'undefined' && (
                <div className="col-span-2"><span className="font-semibold">Aile Uyumu:</span> {
                  '★★★★★'.slice(0, b.familyFriendly) + '☆☆☆☆☆'.slice(0, 5 - b.familyFriendly)
                }</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}



